import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  ArrowRightIcon,
  BeakerIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useActiveStudent } from '../../hooks/useActiveStudent';
import {
  useGetMyInvitationsQuery,
  useGetMyPoliciesQuery,
  useListMyChildMedicationsQuery,
} from '../../store/api/localApi';
import TodayStrip from '../../components/parent/TodayStrip';
import RecentPreview from '../../components/parent/RecentPreview';
import ParentLeftChildBanner from '../../components/parent/ParentLeftChildBanner';
import { buildNeedsYouRows } from './needs-you';

/**
 * /parent/:studentId — the per-kid home.
 *
 * The kid is the frame: name + photo + location subtitle pinned to the
 * top so every action that follows is unambiguously "for this child."
 * Below it, in order: today, what needs the parent, three quick actions,
 * and a two-item preview of recent updates. The child's record (parents,
 * fees, medical, pickup) lives on /parent/:studentId/details.
 */
const ParentChildHome: React.FC = () => {
  const { activeStudent, isLoading, parentHomePath } = useActiveStudent();
  const { data: invitationsResponse } = useGetMyInvitationsQuery();
  const { data: policiesResponse } = useGetMyPoliciesQuery();
  const { data: medicationsResponse } = useListMyChildMedicationsQuery(
    { childId: activeStudent?.id ?? '' },
    { skip: !activeStudent }
  );

  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">
        Loading…
      </div>
    );
  }

  // Defence in depth: ParentRoute already redirects unknown studentIds,
  // but guard here too so the page never tries to render for an unresolved
  // child (which would be a worse UX than a brief redirect).
  if (!activeStudent) {
    return <Navigate to={parentHomePath ?? '/parent'} replace />;
  }

  const needsYou = buildNeedsYouRows({
    child: activeStudent,
    invitations: invitationsResponse?.invitations ?? [],
    policies: policiesResponse?.policies ?? [],
    medications: medicationsResponse?.requirements ?? [],
  });
  // Task 17: a LEFT child is read-only. Write entry points (absence filing)
  // are hidden and the "has left" banner explains why; nothing can be waiting
  // on the parent. The ability layer enforces this regardless.
  const hasLeft = activeStudent.status === 'LEFT';
  const base = `/parent/${activeStudent.id}`;

  return (
    <div className="py-6">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
        {/* Kid header — the frame that disambiguates every action below */}
        <header data-testid="parent-child-header" className="bg-white rounded-lg shadow p-4 sm:p-5">
          <div className="flex items-center gap-4">
            <Avatar name={activeStudent.displayName} size="lg" />
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-gray-900 truncate">
                {activeStudent.displayName}
              </h1>
              <p className="text-sm text-gray-500 truncate">
                {activeStudent.locationName}
                {activeStudent.grade ? ` · ${activeStudent.grade}` : ''}
              </p>
            </div>
          </div>
        </header>

        {/* LEFT-child banner — this child's pages are read-only now. */}
        {hasLeft && (
          <ParentLeftChildBanner
            childName={activeStudent.firstName || activeStudent.displayName}
            locationName={activeStudent.locationName}
          />
        )}

        {/* 1. Today — drop-off/pickup signal for the active kid. Quietly
            renders nothing if the rollup hasn't surfaced this kid yet (e.g.
            fresh registration where data hasn't propagated). */}
        {!activeStudent.badges.registrationPending && <TodayStrip studentId={activeStudent.id} />}

        {/* 2. Needs you — one row per kind of thing waiting on the parent. */}
        {!hasLeft && (
          <section data-testid="needs-you" className="bg-white rounded-lg shadow">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">Needs you</h2>
            </div>
            {needsYou.length === 0 ? (
              <p
                data-testid="needs-you-empty"
                className="px-4 py-4 text-sm text-gray-500 flex items-center gap-2"
              >
                <CheckCircleIcon aria-hidden="true" className="h-5 w-5 text-green-500" />
                All caught up
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {needsYou.map((row) => (
                  <li key={row.key}>
                    <Link
                      to={row.href}
                      data-testid={`needs-you-${row.key}`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50"
                    >
                      <span className="flex-1 text-sm font-medium text-gray-900">{row.label}</span>
                      <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* 3. Quick actions — kid is the frame, so each link goes to the
            per-kid sub-route. */}
        <section
          data-testid="parent-child-quick-actions"
          aria-labelledby="quick-actions-heading"
          className="grid grid-cols-1 sm:grid-cols-3 gap-3"
        >
          <h2 id="quick-actions-heading" className="sr-only">
            Quick actions
          </h2>
          {!hasLeft && (
            <ActionCard
              to={`${base}/calendar?report=1`}
              icon={CalendarDaysIcon}
              title="Report absence"
              subtitle="Pick the days away"
              testId="parent-child-action-absence"
            />
          )}
          {/* Message staff — deliberately NOT hidden for a LEFT child. Unlike
              absence filing (a write against a live enrolment), a conversation
              with the team is how a parent asks the questions that outlive the
              enrolment: a final invoice, a forgotten coat, a reference. The
              backend gates the thread on parent-of-this-child, not on the
              child being ACTIVE, so the surface matches the rule. */}
          <ActionCard
            to={`${base}/threads`}
            icon={ChatBubbleLeftRightIcon}
            title="Message staff"
            subtitle="Ask the team a question"
            testId="parent-child-action-threads"
          />
          {/* Medication stays for a LEFT child: it opens the medication LOG (a
              read), and that page disables its own consent / request buttons. */}
          <ActionCard
            to={`${base}/medications`}
            icon={BeakerIcon}
            title="Medication"
            subtitle="Records and consent"
            testId="parent-child-action-medications"
          />
        </section>

        {/* 4. Recent — a two-item preview of the Updates stream (moments,
            observations and incident reports); "See all" opens the full
            stream. Passive: rendering it never marks anything read. */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-gray-900">Recent</h2>
            <Link
              to={`${base}/updates`}
              data-testid="parent-home-see-updates"
              className="text-xs text-primary-600 hover:text-primary-700"
            >
              See all
            </Link>
          </div>
          <RecentPreview key={activeStudent.id} childId={activeStudent.id} />
        </section>
      </div>
    </div>
  );
};

const Avatar: React.FC<{ name: string; size?: 'md' | 'lg' }> = ({ name, size = 'md' }) => {
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const sizeClasses = size === 'lg' ? 'h-14 w-14 text-lg' : 'h-9 w-9 text-sm';
  return (
    <div
      aria-hidden="true"
      className={`rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-semibold flex-shrink-0 ${sizeClasses}`}
    >
      {initials || '?'}
    </div>
  );
};

const ActionCard: React.FC<{
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  subtitle: string;
  testId?: string;
}> = ({ to, icon: Icon, title, subtitle, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow flex items-center gap-3"
  >
    <div className="h-10 w-10 rounded-md flex items-center justify-center flex-shrink-0 bg-primary-50 text-primary-700">
      <Icon className="h-5 w-5" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold text-gray-900">{title}</div>
      <div className="text-xs text-gray-500 truncate">{subtitle}</div>
    </div>
    <ArrowRightIcon className="h-4 w-4 text-gray-400" />
  </Link>
);

export default ParentChildHome;
